import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { resolveSrc } from "@/lib/api";
import ScreenProtection from "@/components/ScreenProtection";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LogOut, FolderOpen, ImageIcon, Video, ExternalLink, Eye } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_bf707ab1-d809-4aca-a466-4390571c997b/artifacts/6hmyyhtk_23Recurso%203.png";

const typeIcon = (t) => {
  if (t === "image") return <ImageIcon className="h-3.5 w-3.5" />;
  if (t === "video") return <Video className="h-3.5 w-3.5" />;
  return <ExternalLink className="h-3.5 w-3.5" />;
};

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null); // active project
  const [preview, setPreview] = useState(null); // open preview item
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/my/projects");
        if (cancelled) return;
        setProjects(data);
        if (data.length) setActive(data[0]);
      } catch (e) {
        console.warn("failed to load projects", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ScreenProtection username={user?.username || "cliente"}>
      <div className="min-h-screen bg-[#05080f] text-white">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-white/5 bg-[#05080f]/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="logo" className="h-9 w-9 object-contain" draggable={false} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">
                  Playground
                </div>
                <div className="text-sm font-medium text-white/90">
                  Hola, {user?.name || user?.username}
                </div>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={logout}
              className="text-white/70 hover:bg-white/5 hover:text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Salir
            </Button>
          </div>
        </header>

        <main className="mx-auto grid max-w-7xl gap-8 px-6 py-10 lg:grid-cols-[260px_1fr]">
          {/* Sidebar: projects */}
          <aside data-testid="projects-sidebar" className="space-y-2">
            <div className="mb-4 text-[10px] uppercase tracking-[0.4em] text-white/40">
              Tus proyectos
            </div>
            {loading && <div className="text-sm text-white/40">Cargando...</div>}
            {!loading && projects.length === 0 && (
              <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4 text-sm text-white/50">
                Aún no tienes proyectos asignados.
              </div>
            )}
            {projects.map((p) => (
              <button
                key={p.id}
                data-testid={`project-tab-${p.id}`}
                onClick={() => setActive(p)}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all ${
                  active?.id === p.id
                    ? "border-[#1e88ff]/50 bg-[#1e88ff]/10 text-white"
                    : "border-white/5 bg-white/[0.02] text-white/70 hover:border-white/10 hover:bg-white/[0.04]"
                }`}
              >
                <FolderOpen className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{p.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {p.items?.length || 0} vistas
                  </div>
                </div>
              </button>
            ))}
          </aside>

          {/* Project content */}
          <section data-testid="project-content">
            {active ? (
              <div>
                <div className="mb-6">
                  <h1 className="font-serif text-3xl text-white/95 sm:text-4xl">
                    {active.title}
                  </h1>
                  {active.description && (
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60">
                      {active.description}
                    </p>
                  )}
                </div>

                {active.items?.length === 0 && (
                  <div className="rounded-lg border border-white/5 bg-white/[0.02] p-8 text-center text-sm text-white/50">
                    Este proyecto aún no tiene vistas previas.
                  </div>
                )}

                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {active.items?.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => {
                      if (it.type === "url") {
                        window.open(it.src, "_blank", "noopener,noreferrer");
                      } else {
                        setPreview(it);
                      }
                    }}
                      data-testid={`preview-card-${it.id}`}
                      className="group overflow-hidden rounded-xl border border-white/5 bg-white/[0.02] text-left transition-all hover:border-[#1e88ff]/40 hover:bg-white/[0.04]"
                    >
                      <div className="relative aspect-video w-full overflow-hidden bg-black/40">
                        {it.type === "image" && (
                          <img
                            src={resolveSrc(it.src)}
                            alt={it.label || ""}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            draggable={false}
                          />
                        )}
                        {it.type === "video" && (
                          <video
                            src={resolveSrc(it.src)}
                            className="h-full w-full object-cover"
                            muted
                            preload="metadata"
                          />
                        )}
                        {it.type === "url" && (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#0a1224] to-[#05080f]">
                            <ExternalLink className="h-10 w-10 text-[#1e88ff]/70" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-transparent to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                          <div className="flex items-center gap-2 text-xs text-white/90">
                            <Eye className="h-3.5 w-3.5" /> Ver vista previa
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 p-3">
                        <div className="truncate text-sm font-medium text-white/85">
                          {it.label || "Vista previa"}
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-white/60">
                          {typeIcon(it.type)} {it.type}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              !loading && (
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-10 text-center text-white/50">
                  Selecciona un proyecto a la izquierda
                </div>
              )
            )}
          </section>
        </main>

        {/* Preview Dialog */}
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent
            data-testid="preview-dialog"
            className="max-w-5xl border-white/10 bg-[#05080f] text-white"
          >
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-white/90">
                {preview?.label || "Vista previa"}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3">
              {preview?.type === "image" && (
                <img
                  src={resolveSrc(preview.src)}
                  alt={preview.label || ""}
                  className="max-h-[75vh] w-full rounded-md object-contain"
                  draggable={false}
                />
              )}
              {preview?.type === "video" && (
                <video
                  src={resolveSrc(preview.src)}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  className="max-h-[75vh] w-full rounded-md bg-black"
                />
              )}
              {preview?.type === "url" && (
                <iframe
                  src={preview.src}
                  title={preview.label || "preview"}
                  className="h-[75vh] w-full rounded-md border border-white/10 bg-white"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ScreenProtection>
  );
}
